import {announce} from '@github-ui/aria-live'
import {useCallback, useEffect, useRef, type ReactNode} from 'react'
import styles from './PermissionDisplay.module.css'
import {InlineCode} from './InlineCode'
import {ChoiceButton, ChoiceButtonGroup} from '@github-ui/agent-sessions-interface/components/ChoiceButton'
import {UserChoiceCard} from '@github-ui/agent-sessions-interface/components/UserChoiceCard'
import {getPermissionLabel} from '../../utils/permission-prompt-helpers'
import {ChevronLeftIcon, ChevronRightIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'

export interface PermissionDisplayProps {
  /** The kind of permission: 'commands', 'shell', 'write', 'mcp', or a tool name */
  kind: string
  /** The tool requesting permission */
  toolName?: string
  /** Name of the MCP server providing the tool (for kind: "mcp") */
  serverName?: string
  /** What the tool intends to do */
  intention?: string
  /** Command text or filename to display (used when no previewContent is provided) */
  detail?: string
  /** Rich preview content (e.g. a diff preview). When provided, replaces detail. */
  previewContent?: ReactNode
  /** Whether session-level approval can be offered */
  canOfferSessionApproval?: boolean
  /** Command identifiers for session-level approval label */
  commandIdentifiers?: string[]
  /** Callback when user responds (approved, scope) */
  onRespond?: (approved: boolean, scope?: 'once' | 'session') => void
  /** Whether interaction is disabled (e.g., while submitting) */
  disabled?: boolean
  /** Navigation metadata for sessions with multiple unresolved permission prompts */
  promptNavigation?: {
    current: number
    total: number
    onPrevious: () => void
    onNext: () => void
  }
  /** When true, hides container chrome (for embedding in another container) */
  embedded?: boolean
}

const EMPTY_COMMAND_IDENTIFIERS: string[] = []

/** Display a permission request prompt with Allow/Deny choices */
export function PermissionDisplay({
  kind,
  toolName,
  serverName,
  intention,
  detail,
  previewContent,
  canOfferSessionApproval = false,
  commandIdentifiers = EMPTY_COMMAND_IDENTIFIERS,
  onRespond,
  disabled = false,
  promptNavigation,
  embedded = false,
}: PermissionDisplayProps) {
  const handleAllow = useCallback(() => {
    if (disabled) return
    onRespond?.(true)
  }, [disabled, onRespond])

  const handleAllowSession = useCallback(() => {
    if (disabled) return
    onRespond?.(true, 'session')
  }, [disabled, onRespond])

  const handleDeny = useCallback(() => {
    if (disabled) return
    onRespond?.(false)
  }, [disabled, onRespond])

  const {isWrite, isMcp, label} = getPermissionLabel(kind, toolName, serverName)

  // Human-readable name for MCP tools, with fallbacks:
  // 1. `intention` carries the MCP tool's `title` (e.g. "Create or update issue.")
  //    that the MCP server advertises in its tool descriptor.
  // 2. `toolName` is the gate-internal id (e.g. "github-mcp-server-issue_write") —
  //    used when the title is missing/empty.
  // 3. If both are missing, this stays undefined and generic labels are used.
  const mcpDisplayName = isMcp ? intention?.trim() || toolName : undefined

  const sessionLabel =
    canOfferSessionApproval && commandIdentifiers.length > 0
      ? `Allow and don't ask again for \`${commandIdentifiers.join('`, `')}\``
      : isWrite
        ? 'Allow all edits this session'
        : isMcp && mcpDisplayName
          ? `Allow \`${mcpDisplayName}\` for this session`
          : 'Allow for session'

  const allowLabel: ReactNode =
    isMcp && mcpDisplayName ? (
      <InlineCode text={`Allow \`${mcpDisplayName}\``} codeClassName={styles.sessionLabelCode} />
    ) : (
      'Allow'
    )
  const allowAriaLabel = isMcp && mcpDisplayName ? `Allow ${mcpDisplayName}` : 'Allow'
  const title = intention?.trim() || label
  const hasPromptNavigation = promptNavigation !== undefined && promptNavigation.total > 0
  const promptNavigationLabel = hasPromptNavigation
    ? `Permission request ${promptNavigation.current} of ${promptNavigation.total}`
    : undefined
  const canGoToPreviousPrompt = hasPromptNavigation && !disabled && promptNavigation.current > 1
  const canGoToNextPrompt = hasPromptNavigation && !disabled && promptNavigation.current < promptNavigation.total
  const previousPromptNavigationCurrentRef = useRef(promptNavigation?.current)

  useEffect(() => {
    if (!hasPromptNavigation || !promptNavigation) return
    if (previousPromptNavigationCurrentRef.current === promptNavigation.current) return

    previousPromptNavigationCurrentRef.current = promptNavigation.current
    announce(`Permission request ${promptNavigation.current} of ${promptNavigation.total}: ${title}`)
  }, [promptNavigation, hasPromptNavigation, title])

  // MCP tools always offer session-level approval
  const showSessionOption = canOfferSessionApproval || isWrite || isMcp

  const handlePreviousPrompt = useCallback(() => {
    if (!canGoToPreviousPrompt) return
    promptNavigation?.onPrevious()
  }, [canGoToPreviousPrompt, promptNavigation])

  const handleNextPrompt = useCallback(() => {
    if (!canGoToNextPrompt) return
    promptNavigation?.onNext()
  }, [canGoToNextPrompt, promptNavigation])

  const choices: Array<{label: ReactNode; ariaLabel: string; onClick: () => void}> = [
    {
      label: allowLabel,
      ariaLabel: allowAriaLabel,
      onClick: handleAllow,
    },
  ]

  if (showSessionOption) {
    choices.push({
      label: <InlineCode text={sessionLabel} codeClassName={styles.sessionLabelCode} />,
      ariaLabel: sessionLabel.replaceAll('`', ''),
      onClick: handleAllowSession,
    })
  }

  choices.push({
    label: 'Deny',
    ariaLabel: 'Deny',
    onClick: handleDeny,
  })

  return (
    <UserChoiceCard
      title={title}
      embedded={embedded}
      headerButtons={
        hasPromptNavigation ? (
          <div className={styles.promptNavigation} role="group" aria-label={promptNavigationLabel}>
            <IconButton
              icon={ChevronLeftIcon}
              aria-label="Show previous permission request"
              size="small"
              variant="invisible"
              inactive={!canGoToPreviousPrompt}
              aria-disabled={!canGoToPreviousPrompt}
              onClick={handlePreviousPrompt}
            />
            <span
              className={styles.promptNavigationText}
            >{`${promptNavigation.current} of ${promptNavigation.total}`}</span>
            <IconButton
              icon={ChevronRightIcon}
              aria-label="Show next permission request"
              size="small"
              variant="invisible"
              inactive={!canGoToNextPrompt}
              aria-disabled={!canGoToNextPrompt}
              onClick={handleNextPrompt}
            />
          </div>
        ) : undefined
      }
      previewContent={
        previewContent ? previewContent : detail ? <div className={styles.detail}>{detail}</div> : undefined
      }
    >
      <ChoiceButtonGroup>
        {choices.map((choice, index) => (
          <ChoiceButton
            key={choice.ariaLabel}
            number={index + 1}
            label={choice.label}
            ariaLabel={choice.ariaLabel}
            disabled={disabled}
            onClick={choice.onClick}
          />
        ))}
      </ChoiceButtonGroup>
    </UserChoiceCard>
  )
}
